import definitions from "../definitions";
import settings from "../settings";
import * as SAT from 'sat';
import { Emitter } from "pixi-particles";
import { json } from "p3";

export default class Tile extends PIXI.Sprite {
    constructor(texture) {
        super(texture);

        this._collisionEnabled = false;
        this._collisionWith = [];
        this._collisionLeft = 0;
        this._collisionRight = 0;
        this._collisionTop = 0;
        this._collisionBottom = 0;
        this._collisionDebug = null;
        this._collisionDebugAlpha = 0.4;
        this._colliderVertices = [];
        this._colliders = [];
        this._collisionRectangles = [];
        this._collisionType = definitions.COLLISION_TYPE.RECT;

        this._fx = null;

        this.initCollider();
    }

    update() {
        this._fx && this._fx.update(window.dt);

        this._collisionWith.length = 0;
        this.updateCollisionDebug();
    }

    emitFx(textures = [], data) {
        this._fx = this._createFx(textures, data);
    }

    _createFx(textures = [], data) {
        const emitter = new Emitter(this, textures, json(data));
        emitter.autoUpdate = false;
        emitter.updateSpawnPos(this.width / 2, this.height / 2);
        return emitter;
    }

    /**
     * @param {number} colliderType 
     */
    initCollider() {
        switch (this._collisionType) {
            case definitions.COLLISION_TYPE.CIRCLE: 
                break;
            case definitions.COLLISION_TYPE.RECT:
                if (this._collisionRectangles.length === 0) {
                    this._colliders[0] = new SAT.Box(new SAT.Vector(this.collisionX, this.collisionY), 
                                                     this.collisionWidth, this.collisionHeight).toPolygon();
                } else {
                    for (let i = 0; i < this._collisionRectangles.length; ++i) {
                        const rectangleCollider = this._collisionRectangles[i];
                        const x = rectangleCollider.collisionRight * this.width;
                        const y = rectangleCollider.collisionBottom * this.height;
                        const width = this.width - (rectangleCollider.collisionLeft * this.width);
                        const height = this.height - (rectangleCollider.collisionTop * this.height);
                        
                        this._colliders[i] = new SAT.Box(new SAT.Vector(this.x + (rectangleCollider.collisionLeft * this.width),
                                                         this.y + (rectangleCollider.collisionTop * this.height)), 
                                                         width - x, height - y).toPolygon();
                    }
                }
                break;
            case definitions.COLLISION_TYPE.POLYGON:
                const vertices = [];
                for (let i = 0; i < this._colliderVertices.length; ++i) {
                    const vertex = this._colliderVertices[i];
                    vertices[i] = new SAT.Vector(this.x + vertex.x, this.y + vertex.y);
                }
     
                this._colliders[0] = new SAT.Polygon(new SAT.Vector(this.collisionX, this.collisionY), vertices);
                break;
        }
    }

    updateCollisionDebug() {
        if (!this._collisionDebug && settings.WORLD_ENABLE_COLLISION_DEBUG) {
            this._collisionDebug = new PIXI.Graphics();
        } else if (this._collisionDebug && !settings.WORLD_ENABLE_COLLISION_DEBUG) {
            this._collisionDebug.destroy();
            this._collisionDebug = null;
        }

        if (this._collisionDebug) {
            this._collisionDebug.clear();
            if (this._collisionEnabled) {
                if (settings.WORLD_ENABLE_COLLISION_DEBUG) {
                    let color = 0xFF0000;
                    if (this.isColliding) {
                        color = 0x00FF00;
                    }
     
                    switch (this._collisionType) {
                        case definitions.COLLISION_TYPE.RECT:
                            if (this.collisionRectangles.length === 0) {
                                this._collisionDebug.beginFill(color, this._collisionDebugAlpha);
                                this._collisionDebug.drawRect(this.collisionLocalX, this.collisionLocalY, this.collisionWidth, this.collisionHeight);
                                this._collisionDebug.endFill();
                            } 
                            else if (this.collisionRectangles.length > 0) {
                                for (let i = 0; i < this.collisionRectangles.length; ++i) {
                                    const rectangleCollider = this.collisionRectangles[i];
                                    const x = rectangleCollider.collisionRight * this.width;
                                    const y = rectangleCollider.collisionBottom * this.height;
                                    const width = this.width - (rectangleCollider.collisionLeft * this.width);
                                    const height = this.height - (rectangleCollider.collisionTop * this.height);
                                    
                                    this._collisionDebug.beginFill(color, this._collisionDebugAlpha);
                                    this._collisionDebug.drawRect(rectangleCollider.collisionLeft * this.width, 
                                                                  rectangleCollider.collisionTop * this.height, 
                                                                  width - x, height - y);
                                    this._collisionDebug.endFill();
                                }
                            }

                            break;
                        case definitions.COLLISION_TYPE.POLYGON:
                            this._collisionDebug.beginFill(color, this._collisionDebugAlpha);
                            this._collisionDebug.drawPolygon(new PIXI.Polygon(this._colliderVertices));
                            this._collisionDebug.endFill();
                            break;
                        default:
                            break;
                    }

                    this.addChild(this._collisionDebug);
                }
            }
        }
    }

    /**
     * @param {Entity} entity
     * @param {P3.Vector2} v
     */
    onCollisionEntity(entity, v) {
        this._collisionWith.push(entity);
        this.updateCollisionDebug();
    }

    /**
     * @returns {boolean}
     */
    get collisionEnabled() {
        return this._collisionEnabled;
    }

    /**
     * @param {boolean} value
     */
    set collisionEnabled(value) {
        this._collisionEnabled = value;
        this.updateCollisionDebug();
    }

    /**
     * @returns {boolean}
     */
    get isColliding() {
        return this._collisionWith.length > 0;
    }

    /**
     * @returns {number}
     */
    get collisionLeft() {
        return this._collisionLeft;
    }

    /**
     * @param {number} value
     */
    set collisionLeft(value) {
        this._collisionLeft = value;
        this.updateCollisionDebug();
    }

    /**
     * @returns {number}
     */
    get collisionRight() {
        return this._collisionRight;
    }

    /**
     * @param {number} value
     */
    set collisionRight(value) {
        this._collisionRight = value;
        this.updateCollisionDebug();
    }

    /**
     * @returns {number}
     */
    get collisionTop() {
        return this._collisionTop;
    }

    /**
     * @param {number} value
     */
    set collisionTop(value) {
        this._collisionTop = value;
        this.updateCollisionDebug();
    }

    /**
     * @returns {number}
     */
    get collisionBottom() {
        return this._collisionBottom;
    }

    /**
     * @param {number} value
     */
    set collisionBottom(value) {
        this._collisionBottom = value;
        this.updateCollisionDebug();
    }

    /**
     * @returns {number}
     */
    get collisionLocalX() {
        return this._collisionLeft * this.width;
    }

    /**
     * @returns {number}
     */
    get collisionLocalY() {
        return this._collisionTop * this.height;
    }

    /**
     * @returns {number}
     */
    get collisionX() {
        return this.x + this.collisionLocalX;
    }

    /**
     * @returns {number}
     */
    get collisionY() {
        return this.y + this.collisionLocalY;
    }

    /**
     * @returns {number}
     */
    get collisionWidth() {
        const x = this._collisionRight * this.width;
        const width = this.width - this.collisionLocalX;
        return width - x;
    }

    /**
     * @returns {number}
     */
    get collisionHeight() {
        const y = this._collisionBottom * this.height;
        const height = this.height - this.collisionLocalY;
        return height - y;
    }

    /**
     * @returns {number}
     */
    get collisionDebugAlpha() {
        return this._collisionDebugAlpha;
    }

    /**
     * @param {number} value
     */
    set collisionDebugAlpha(value) {
        this._collisionDebugAlpha = value;
        this.updateCollisionDebug();
    }

    /**
     * @returns {Array<PIXI.Point>}
     */
    get collisionVertices() {
        return this._colliderVertices;
    }

    /**
     * @param {number} value
     */
    set collisionVertices(value) {
        this._colliderVertices = value;
    }

    /**
     * @returns {SAT.Box | SAT.Polygon | SAT.Circle}
     */
    get colliders() {
        return this._colliders;
    }

    /**
     * @returns {number}
     */
    get collisionType() {
        return this._collisionType;
    }
    
    /**
     * @param {number} value
     */
    set collisionType(value) {
        this._collisionType = value;
    }

    /**
     * @returns {Array<Object>}
     */
    get collisionRectangles() {
        return this._collisionRectangles;
    }

    /**
     * @param {number} value
     */
    set collisionRectangles(value) {
        this._collisionRectangles = value;
    } 
}